import './SpikeChart.scss';
import cn from 'classnames';
import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as Dfns from 'date-fns/fp';
import { Tooltip as RechartsTooltip } from 'recharts';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { MdClose } from 'react-icons/md';
import { LINEAR } from '@blisspointmedia/bpm-types/dist/ScpChannels';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { Button } from '@Components/Button/Button';
import { DatePicker } from '@Components/DatePicker';
import { Background, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { EntityKind, EntityLogo } from '@Components/EntityLogo';
import { Img } from '@Components/Img';
import { KpiPicker, useKpiPickerData } from '@Components/KpiPicker';
import { getCreativeThumbnail } from '@Components/MetricsTable/metrics-table-utils';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { Page } from '@Components/Page';
import { PAGE_NAMES } from '@Components/Page/page-constants';
import { FullPageSpinner } from '@Components/Spinner';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { CreativeMap, useCreativeMap } from '@src/redux/creative';
import { useSetError } from '@src/redux/modals';
import { Brand60 } from '@src/utils/colors';
import { download, downloadJSONToCSV } from '@src/utils/download-utils';
import { S3SignedUrlFetch, assertPresignedOk } from '@src/utils/fetch-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { DotRenderProps, InnerSpikeChart } from './InnerSpikeChart';
import {
  CreativeInfo,
  DOT_BORDER,
  EMPTY_SPIKE_CHART_DATA,
  SpikeChartData,
  SpotCoordinate,
  SpotTableRow,
  TABLE_DATE_FORMAT,
  TOOLTIP_DATE_FORMAT
} from './spike-chart-constants';
import { useSpikeChartData, useSpotAttributionExists } from './use-spike-chart-data';
import {
  buildSpotTableData,
  findIsciByCreativeName,
  formatSpikeCsvRows,
  getCutoffDate,
  getDefaultWeek,
  getTestIsci,
  pngUri
} from './spike-chart-utils';

type TooltipCreativeInfo = CreativeInfo & { fromTable?: boolean };

export const SpikeChart = () => {
  const company = useLocationCompany();
  const companyId = company ?? '';
  const isTestCompany = Boolean(company && TEST_COMPANIES.includes(company));
  const setError = useSetError();

  const { spike_kpis: spikeKpis, kpis } = useCompanyInfo(companyId);
  const { creativeMap } = useCreativeMap({ company: companyId, mediaTypes: [LINEAR] });

  const [kpi, setKpi] = useState<string>();
  const { kpiMap } = useKpiPickerData({ company: companyId, mediaType: LINEAR });
  const hasSpikeKpiMatch = useMemo(
    () => Boolean(spikeKpis?.some(spikeKpi => kpiMap[spikeKpi])),
    [spikeKpis, kpiMap]
  );
  const kpiFilter = useCallback(
    (kpiId: string) => (hasSpikeKpiMatch ? Boolean(spikeKpis?.includes(kpiId)) : true),
    [hasSpikeKpiMatch, spikeKpis]
  );
  const yAxisTitle = useMemo(() => kpis.find(k => k.id === kpi)?.name, [kpis, kpi]);

  const [week, setWeek] = useState<string>(getDefaultWeek);
  const cutoffDate = useMemo(() => getCutoffDate(), []);

  const { data, isLoading, error } = useSpikeChartData(companyId, week, kpi, isTestCompany);
  const { data: spotAttributionExists = false } = useSpotAttributionExists(kpi);

  const [left, setLeft] = useState<number | string>('dataMin');
  const [right, setRight] = useState<number | string>('dataMax');
  const [top, setTop] = useState<number | string>('auto');
  const [bottom, setBottom] = useState(0);
  const [selectedPoint, setSelectedPoint] = useState<number>();
  const [creativeTooltipProps, setCreativeTooltipProps] = useState<TooltipCreativeInfo>();
  const [coordinateMap, setCoordinateMap] = useState<Record<number, SpotCoordinate>>({});

  useEffect(() => {
    if (error) {
      setError({ message: error.message, reportError: error });
    }
  }, [error, setError]);

  // Reset the y-axis zoom top whenever the selected week/KPI changes.
  useEffect(() => {
    setTop('auto');
  }, [kpi, week]);

  const downloadSpotAttribution = useCallback(async () => {
    if (!kpi) {
      return;
    }
    const response = await S3SignedUrlFetch(`bpm-ml-data/v4/${kpi}/latest/spot_attribution.csv.gz`);
    try {
      assertPresignedOk(response, 'Spot attribution download failed');
    } catch (e) {
      setError({ message: e.message, reportError: e });
      return;
    }
    const blob = await response.blob();
    download(blob, `spot_attribution_${kpi}.csv`, 'text/csv');
  }, [kpi, setError]);

  const tableData = useMemo(() => buildSpotTableData(data), [data]);

  const downloadCSV = useCallback(() => {
    downloadJSONToCSV(formatSpikeCsvRows(tableData), `${week}_${kpi}`);
  }, [tableData, week, kpi]);

  const downloadPNG = useCallback(() => {
    download(pngUri('spike_chart'), `${kpi}_spike_chart`);
  }, [kpi]);

  const downloadMenuOptions = useMemo(
    () => (spotAttributionExists ? ['CSV', 'PNG', 'Spot Attribution'] : ['CSV', 'PNG']),
    [spotAttributionExists]
  );
  const downloadHandlers = useMemo(
    () =>
      spotAttributionExists
        ? [downloadCSV, downloadPNG, downloadSpotAttribution]
        : [downloadCSV, downloadPNG],
    [spotAttributionExists, downloadCSV, downloadPNG, downloadSpotAttribution]
  );

  const zoomOut = useCallback(() => {
    setLeft('dataMin');
    setRight('dataMax');
    setBottom(0);
    setTop('auto');
  }, []);

  const renderDot = useCallback(
    ({ cx, cy, payload: rawPayload }: DotRenderProps) => {
      const payload = rawPayload as SpikeChartData;
      if (!payload.dotValue) {
        return null;
      }
      return (
        <circle
          key={cx}
          cx={cx}
          cy={cy}
          r={5}
          fill={Brand60}
          fillOpacity={1}
          stroke={DOT_BORDER}
          strokeWidth={3}
          onClick={() => {
            if (selectedPoint === payload.time) {
              setSelectedPoint(undefined);
              setCreativeTooltipProps(undefined);
              return;
            }
            setSelectedPoint(payload.time);
          }}
          onMouseOver={() => setCreativeTooltipProps(payload.creativeInfo[0])}
          onMouseOut={() => setCreativeTooltipProps(undefined)}
        />
      );
    },
    [selectedPoint]
  );

  const tooltipProps = useMemo(() => {
    if (creativeTooltipProps?.fromTable) {
      return { position: coordinateMap[creativeTooltipProps.timeslot] };
    }
    return {};
  }, [creativeTooltipProps, coordinateMap]);

  const hoverRow = useCallback(
    (row: SpotTableRow) => setCreativeTooltipProps({ ...row, fromTable: true }),
    []
  );
  const unhoverRow = useCallback(() => setCreativeTooltipProps(undefined), []);

  const scrollToRef = useRef<(row: number, col: number) => void>(() => {});

  useLayoutEffect(() => {
    const rowIndex = tableData.findIndex(row => row.timeslot === selectedPoint);
    if (rowIndex >= 0) {
      scrollToRef.current?.(rowIndex, 0);
    }
  }, [tableData, selectedPoint]);

  const headers = useMemo<Header[]>(
    () => [
      {
        label: 'Spot Time',
        name: 'time',
        width: 275,
        nonInteractive: true,
        renderer: (row: SpotTableRow) => {
          const dataTimeStr = row.time?.split(' ')[0] ?? '';
          const timeSuffix =
            row.time?.split(' ').length > 1 ? row.time.replace(dataTimeStr, '') : '';
          return (
            <SpotRowCell
              isSelected={row.timeslot === selectedPoint}
              odd={row.odd}
              onHover={() => hoverRow(row)}
              onUnhover={unhoverRow}
            >
              {dataTimeStr && Dfns.format(TABLE_DATE_FORMAT, Dfns.parseISO(dataTimeStr))}{' '}
              {timeSuffix}
            </SpotRowCell>
          );
        }
      },
      {
        label: 'Network',
        name: 'network',
        width: 180,
        nonInteractive: true,
        renderer: (row: SpotTableRow) => (
          <SpotRowCell
            className='logo-container'
            isSelected={row.timeslot === selectedPoint}
            odd={row.odd}
            onHover={() => hoverRow(row)}
            onUnhover={unhoverRow}
          >
            <OverlayTrigger placement='right' overlay={<Tooltip>{row.network}</Tooltip>}>
              <div className='network-logo'>
                <EntityLogo
                  kind={EntityKind.NETWORK}
                  id={row.network}
                  size={Size.MD}
                  showFallbackName
                />
              </div>
            </OverlayTrigger>
            {row.avail === 'L' && <div className='avail'>({row.avail})</div>}
          </SpotRowCell>
        )
      },
      {
        label: 'Cost',
        name: 'cost',
        width: 100,
        nonInteractive: true,
        renderer: (row: SpotTableRow) => (
          <SpotRowCell
            isSelected={row.timeslot === selectedPoint}
            odd={row.odd}
            onHover={() => hoverRow(row)}
            onUnhover={unhoverRow}
          >
            <NumberFormatter type='$0' value={row.cost} />
          </SpotRowCell>
        )
      },
      {
        label: 'Program',
        name: 'program',
        flex: 1,
        nonInteractive: true,
        renderer: (row: SpotTableRow) => (
          <SpotRowCell
            isSelected={row.timeslot === selectedPoint}
            odd={row.odd}
            onHover={() => hoverRow(row)}
            onUnhover={unhoverRow}
          >
            {row.program}
          </SpotRowCell>
        )
      },
      {
        label: 'Creative',
        name: 'creative',
        flex: 1,
        nonInteractive: true,
        renderer: (row: SpotTableRow) => {
          const isci = isTestCompany
            ? getTestIsci(row.creative)
            : findIsciByCreativeName(creativeMap ?? {}, row.creative);
          return (
            <SpotRowCell
              className='creative-container'
              isSelected={row.timeslot === selectedPoint}
              odd={row.odd}
              onHover={() => hoverRow(row)}
              onUnhover={unhoverRow}
            >
              <div className='creative-image'>
                <Img
                  src={getCreativeThumbnail(companyId, isci ?? '')}
                  unloader={<div className='creative-image-unloader'>No Thumbnail</div>}
                />
              </div>
              <div className='creative-name'>
                {row.creative} ({row.length}s)
              </div>
            </SpotRowCell>
          );
        }
      }
    ],
    [selectedPoint, hoverRow, unhoverRow, isTestCompany, creativeMap, companyId]
  );

  return (
    <Page
      title={PAGE_NAMES.SPIKE_CHART}
      pageType={PAGE_NAMES.SPIKE_CHART}
      isLoading={isLoading}
      actions={
        <div className='spike-chart-actions'>
          <div data-testid='kpi-picker'>
            <KpiPicker
              company={companyId}
              mediaType={LINEAR}
              filter={kpiFilter}
              onChange={setKpi}
              noBreak
              className='spike-chart-kpi-picker'
            />
          </div>
          <div className='right-aligned-actions'>
            <div data-testid='week-picker'>
              <DatePicker
                isSingle
                size={Size.LG}
                background={Background.DARK}
                range={{ start: week, end: week }}
                isOutsideRange={date => date > cutoffDate}
                isDayBlocked={date => Dfns.getDay(Dfns.parseISO(date)) !== 1}
                onChange={({ start }) => {
                  if (!start) {
                    return;
                  }
                  setSelectedPoint(undefined);
                  setCreativeTooltipProps(undefined);
                  setWeek(start);
                }}
              />
            </div>
            <div data-testid='download-dropdown'>
              <DownloadDropdown
                size={Size.LG}
                menuOptions={downloadMenuOptions}
                onClickOptions={downloadHandlers}
              />
            </div>
          </div>
        </div>
      }
    >
      <div className='spike-chart-page-container'>
        <SpikeChartBody data={data}>
          <div
            className={cn('spike-chart', { 'hover-tooltip-mode': Boolean(creativeTooltipProps) })}
            id='spike_chart'
            data-testid='spike-chart'
          >
            <AutoSizer
              renderProp={({ width = 0, height = 0 }) => (
                <InnerSpikeChart
                  bottom={bottom}
                  chartData={data ?? EMPTY_SPIKE_CHART_DATA}
                  grid={{ vertical: false, stroke: '#b9bed2', strokeWidth: 0.35 }}
                  height={height}
                  left={left}
                  margin={{ top: 55, bottom: 30, left: 25, right: 50 }}
                  onCoordinates={setCoordinateMap}
                  rechartsToolTip={
                    <RechartsTooltip
                      {...tooltipProps}
                      isAnimationActive={false}
                      // eslint-disable-next-line react/no-unstable-nested-components -- recharts render prop closes over parent state (creativeTooltipProps, creativeMap, coordinateMap, left, right); hoisting would require threading all of them through
                      content={({ payload }) => (
                        <SpikeTooltip
                          payload={
                            payload as unknown as
                              { payload: { formattedTime: string; kpiCount: number } }[] | undefined
                          }
                          creativeTooltipProps={creativeTooltipProps}
                          creativeMap={creativeMap ?? {}}
                          isTestCompany={isTestCompany}
                          coordinateMap={coordinateMap}
                          left={left}
                          right={right}
                          company={companyId}
                        />
                      )}
                    />
                  }
                  renderDot={renderDot}
                  right={right}
                  setBottom={setBottom}
                  setLeft={setLeft}
                  setRight={setRight}
                  setTop={setTop}
                  top={top}
                  width={width}
                  xAxisPadding={{ left: 25, right: 25 }}
                  yAxisTickCount={5}
                  yAxisTitle={yAxisTitle}
                />
              )}
            />
            <div className='spike-chart-legend'>
              {left === 'dataMin' && right === 'dataMax' ? (
                <div className='legend-explainer'>* Click and drag bar to zoom in</div>
              ) : (
                <Button
                  className='zoom-out-button'
                  onClick={zoomOut}
                  leadingElement={<MdClose size={22} className='exit-icon' />}
                >
                  Reset to Default
                </Button>
              )}
            </div>
          </div>
          <div className='spot-table' data-testid='spot-table'>
            <BPMTable
              alternateColors={false}
              scrollToRef={scrollToRef}
              data={tableData}
              filterBar={false}
              headers={headers}
            />
          </div>
        </SpikeChartBody>
      </div>
    </Page>
  );
};

interface SpikeChartBodyProps {
  data?: SpikeChartData[];
  children: ReactNode;
}

const SpikeChartBody = ({ data, children }: SpikeChartBodyProps) => {
  if (!data) {
    return <FullPageSpinner />;
  }
  if (data.length === 0) {
    return (
      <div className='no-spike-data' data-testid='no-spike-data'>
        No data for the selected week.
      </div>
    );
  }
  return <>{children}</>;
};

interface SpotRowCellProps {
  className?: string;
  isSelected: boolean;
  odd: boolean;
  onHover: () => void;
  onUnhover: () => void;
  children: ReactNode;
}

const SpotRowCell = ({
  className,
  isSelected,
  odd,
  onHover,
  onUnhover,
  children
}: SpotRowCellProps) => (
  <div
    className={cn(className, { 'selected-spot': isSelected, odd })}
    onMouseOver={onHover}
    onMouseOut={onUnhover}
    onFocus={onHover}
    onBlur={onUnhover}
  >
    {children}
  </div>
);

interface SpikeTooltipProps {
  payload?: { payload: { formattedTime: string; kpiCount: number } }[];
  creativeTooltipProps?: TooltipCreativeInfo;
  creativeMap: CreativeMap;
  isTestCompany: boolean;
  coordinateMap: Record<number, SpotCoordinate>;
  left: number | string;
  right: number | string;
  company: string;
}

const SpikeTooltip = ({
  payload,
  creativeTooltipProps,
  creativeMap,
  isTestCompany,
  coordinateMap,
  left,
  right,
  company
}: SpikeTooltipProps) => {
  const time = useMemo(() => {
    if (payload?.length) {
      return Dfns.format(
        TOOLTIP_DATE_FORMAT,
        new Date(payload[0].payload.formattedTime.split(' ')[0])
      );
    }
    return null;
  }, [payload]);

  if (creativeTooltipProps) {
    if (
      creativeTooltipProps.fromTable &&
      (creativeTooltipProps.timeslot > Number(right) ||
        creativeTooltipProps.timeslot < Number(left))
    ) {
      return null;
    }

    const isci = isTestCompany
      ? getTestIsci(creativeTooltipProps.creative)
      : findIsciByCreativeName(creativeMap, creativeTooltipProps.creative);

    const isFlipped =
      creativeTooltipProps.fromTable &&
      (coordinateMap[creativeTooltipProps.timeslot]?.x ?? 0) > 0.65 * window.innerWidth;

    const creativeTimeStr = creativeTooltipProps.time.split(' ')[0];
    const timeSuffix =
      creativeTooltipProps.time.split(' ').length > 1
        ? creativeTooltipProps.time.replace(creativeTimeStr, '')
        : '';

    return (
      <div className={cn('creative-tooltip', { 'flip-tooltip': isFlipped })}>
        <div className='creative-thumbnail'>
          <Img
            src={getCreativeThumbnail(company, isci ?? '')}
            unloader={<div className='tooltip-creative-image-unloader'>No Thumbnail</div>}
          />
        </div>
        <div className='spot-info'>
          <div className='spot-network'>
            <EntityLogo
              kind={EntityKind.NETWORK}
              id={creativeTooltipProps.network}
              size={Size.MD}
              showFallbackName
            />
          </div>
          <div className='spot-time'>
            {Dfns.format(TABLE_DATE_FORMAT, Dfns.parseISO(creativeTimeStr))}
            {timeSuffix}
          </div>
          <div className='kpi'>
            <span className='kpi-label'>KPI: </span>
            <span className='kpi-count'>{creativeTooltipProps.kpi}</span>
          </div>
        </div>
      </div>
    );
  }

  const count = payload?.length ? payload[0].payload.kpiCount : undefined;

  return (
    <div className='hover-tooltip'>
      <div className='time'>
        <span className='time-label'>Time: </span>
        <span className='time-date'>{time}</span>
      </div>
      <div className='kpi'>
        <span className='kpi-label'>KPI: </span>
        <span className='kpi-count'>{count}</span>
      </div>
    </div>
  );
};
